import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Sparkle, UploadSimple, Check, ArrowRight, ArrowLeft, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { OBJETS, libelleCR, libelleCompteRendu } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDT(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + ' ' + new Date(s).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return s; }
}
function formatD(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; }
}
function truncate(s, n = 100) { s = s || ''; return s.length > n ? s.substring(0, n) + '…' : s; }

// A2 — réunion proposée par défaut : la plus récente (jusqu'à demain) qui n'a pas encore de CR.
export function reunionParDefaut(reunions) {
  const demain = new Date(); demain.setDate(demain.getDate() + 1);
  const limite = demain.toISOString().slice(0, 10);
  const candidates = (reunions || [])
    .filter((r) => r && r.date_debut && String(r.date_debut).slice(0, 10) <= limite && !r.compte_rendu_id)
    .sort((a, b) => String(b.date_debut).localeCompare(String(a.date_debut)));
  return candidates[0]?.id || 'none';
}

export default function CreateCRCDialog({ chantierId, open, onClose, onCreated, onRequestImport, reunionInitiale = null }) {
  // Wizard step 0 = choix mode, 1/2/3 = wizard IA
  // A2 — ouvert depuis une réunion : directement l'étape 1, réunion liée pré-sélectionnée
  const [step, setStep] = useState(0);

  // Step 1 : paramètres
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState(new Date().toISOString().split('T')[0]);
  const [reunionId, setReunionId] = useState('none');
  const [reunions, setReunions] = useState([]);
  const [nextNumero, setNextNumero] = useState(1);
  const [lastCrc, setLastCrc] = useState(null);
  const [prDate, setPrDate] = useState('');
  const [prHeure, setPrHeure] = useState('10:00');
  const [prLieu, setPrLieu] = useState('');
  const [prOrdre, setPrOrdre] = useState('');

  // Step 2 : collecte + sélection
  const [collecting, setCollecting] = useState(false);
  const [collected, setCollected] = useState(null);
  const [selJournal, setSelJournal] = useState(new Set());
  const [selPhotos, setSelPhotos] = useState(new Set());
  const [selReserves, setSelReserves] = useState(new Set());
  const [selFactures, setSelFactures] = useState(new Set());
  const [includePlanning, setIncludePlanning] = useState(true);
  const [includeDetailTaches, setIncludeDetailTaches] = useState(true);
  // M6 — Mode pool éléments validés terrain vs sélection manuelle
  const [usePoolMode, setUsePoolMode] = useState(false);
  const [poolStats, setPoolStats] = useState(null);

  // Step 3 : notes architecte
  const [decisions, setDecisions] = useState('');
  const [instructions, setInstructions] = useState('');
  const [observations, setObservations] = useState('');
  const [pointsSurveille, setPointsSurveille] = useState('');

  const [generating, setGenerating] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) loadContext(); }, [open, chantierId, reunionInitiale]);

  const loadContext = async () => {
    try {
      const [evts, meta, ch] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/evenements`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/crc/next-numero`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      const liste = Array.isArray(evts.data) ? evts.data : [];
      setReunions(liste);
      setReunionId(reunionInitiale || reunionParDefaut(liste));
      if (reunionInitiale) setStep(1);
      setNextNumero(meta.data.next_numero);
      setLastCrc(meta.data.last_crc);
      // Date début par défaut : jour+1 après dernier CRC, sinon J-7
      if (meta.data.last_crc && meta.data.last_crc.date) {
        const d = new Date(meta.data.last_crc.date);
        d.setDate(d.getDate() + 1);
        setDateDebut(d.toISOString().split('T')[0]);
      } else {
        const d = new Date(); d.setDate(d.getDate() - 7);
        setDateDebut(d.toISOString().split('T')[0]);
      }
      // Prochaine réunion : défaut dans 7 jours, lieu = adresse chantier
      const next = new Date(); next.setDate(next.getDate() + 7);
      setPrDate(next.toISOString().split('T')[0]);
      const adr = ch.data?.adresse || ch.data?.address || '';
      setPrLieu(adr ? `Sur site — ${adr}` : 'Sur site');
    } catch { /* silent */ }
  };

  const goToStep2 = async (forceFromPool = null) => {
    if (!dateDebut || !dateFin) { toast.error('Sélectionnez la période'); return; }
    setCollecting(true);
    try {
      // A2 / B1 — seul un booléen force le mode ; un événement de clic est ignoré.
      const fromPool = typeof forceFromPool === 'boolean' ? forceFromPool : usePoolMode;
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/crc/collecter`, {
        date_debut: dateDebut, date_fin: dateFin, numero_crc: nextNumero,
        reunion_id: reunionId === 'none' ? '' : reunionId,
        from_pool: fromPool,  // M6
      }, { withCredentials: true });
      setCollected(data);
      setPoolStats(data.pool_stats || null);
      // M6 — Mode pool : pré-cocher uniquement les items auto_selected (déjà inclus_in_crc=true côté terrain).
      // Mode manuel : tout cocher comme avant.
      if (fromPool) {
        setSelJournal(new Set((data.journal_textes || []).filter(j => j.auto_selected).map(j => j.id)));
        setSelPhotos(new Set((data.journal_photos || []).filter(p => p.auto_selected).map(p => p.id)));
      } else {
        setSelJournal(new Set((data.journal_textes || []).map(j => j.id)));
        setSelPhotos(new Set((data.journal_photos || []).map(p => p.id)));
      }
      setSelReserves(new Set([...(data.reserves_ouvertes || []), ...(data.reserves_levees_periode || [])].map(r => r.id)));
      setSelFactures(new Set((data.factures || []).map(f => f.id)));
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de collecte');
    } finally {
      setCollecting(false);
    }
  };

  // M6 — Bascule mode pool ↔ manuel pendant la session (recharge la collecte)
  const switchPoolMode = async (toPool) => {
    setUsePoolMode(toPool);
    await goToStep2(toPool);
    if (toPool) {
      toast.success('Mode pool terrain activé — éléments validés pré-cochés');
    } else {
      toast.info('Mode sélection manuelle activé');
    }
  };

  const toggleSet = (setter, id) => setter(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = (setter, items) => setter(prev => {
    const all = items.every(i => prev.has(i.id));
    return all ? new Set() : new Set(items.map(i => i.id));
  });

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = {
        date_debut: dateDebut, date_fin: dateFin,
        numero_crc: nextNumero,
        reunion_id: reunionId === 'none' ? '' : reunionId,
        from_pool: usePoolMode,  // M6
        selected_journal_ids: Array.from(selJournal),
        selected_photo_ids: Array.from(selPhotos),
        selected_reserve_ids: Array.from(selReserves),
        selected_facture_ids: Array.from(selFactures),
        include_planning: includePlanning,
        include_detail_taches: includeDetailTaches,
        notes_architecte: { decisions, instructions, observations, points_a_surveiller: pointsSurveille },
        prochaine_reunion: { date: prDate, heure: prHeure, lieu: prLieu, ordre_du_jour: prOrdre },
      };
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/crc/generer-auto`, payload, { withCredentials: true });
      // A2 (T1) — aucune mention technique : un texte préparé sans l'Assistant est signalé
      // comme « à compléter », il reprend les notes, les présents et les actions validées.
      if (data.redaction_incomplete) {
        toast.warning(`${libelleCR(nextNumero)} préparé : l'Assistant n'a pas pu rédiger l'avancement. Complétez les parties « À compléter » avant la diffusion.`);
      } else {
        toast.success(`${libelleCR(nextNumero)} rédigé — relisez-le avant la diffusion`);
      }
      onCreated(data);
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    setStep(0); setCollected(null);
    setSelJournal(new Set()); setSelPhotos(new Set()); setSelReserves(new Set()); setSelFactures(new Set());
    setDecisions(''); setInstructions(''); setObservations(''); setPointsSurveille('');
    setPrOrdre('');
    setUsePoolMode(false); setPoolStats(null);  // M6
    setReunionId('none');
    onClose();
  };

  const dialogTitle = useMemo(() => {
    if (step === 0) return 'Nouveau Compte Rendu de Chantier';
    if (step === 1) return `${libelleCompteRendu(nextNumero)} — 1/3 · Paramètres`;
    if (step === 2) return `${libelleCompteRendu(nextNumero)} — 2/3 · Contenu à inclure`;
    if (step === 3) return `${libelleCompteRendu(nextNumero)} — 3/3 · Notes de l'architecte`;
    return OBJETS.compteRendu;
  }, [step, nextNumero]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="crc-wizard">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }} className="text-2xl">
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setStep(1)}
                className="border-2 border-[#E0E0DB] hover:border-[#C4773B] hover:bg-[#FDF6EC] p-4 text-left transition-colors"
                data-testid="crc-mode-ia"
              >
                <Sparkle size={22} className="text-[#C4773B] mb-2" weight="fill" />
                <p className="font-semibold text-sm text-[#2C2C2C]">Rédiger avec l'Assistant</p>
                <p className="text-xs text-[#6B6B6B] mt-1">En 3 étapes : paramètres → sélection → notes</p>
              </button>
              {/* CHA-14 — un seul chemin d'import : le dialogue « Importer CR antérieurs »
                  (POST /crc/import-pdf, qui crée un vrai CRC avec ses points). L'ancien
                  formulaire passait par /upload et produisait un simple document. */}
              {onRequestImport && (
                <button
                  onClick={() => { handleClose(); onRequestImport(); }}
                  className="border-2 border-[#E0E0DB] hover:border-[#1E3A5F] p-4 text-left transition-colors"
                  data-testid="crc-mode-import"
                >
                  <UploadSimple size={22} className="text-[#1E3A5F] mb-2" weight="bold" />
                  <p className="font-semibold text-sm text-[#2C2C2C]">Importer un CR existant (PDF)</p>
                  <p className="text-xs text-[#6B6B6B] mt-1">Lecture automatique du PDF : le CR et ses points sont créés.</p>
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP 1 : Paramètres */}
        {step === 1 && (
          <div className="space-y-5" data-testid="crc-step-1">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">N° du compte rendu</Label>
              <Input value={libelleCR(nextNumero)} disabled className="rounded-none bg-[#FAFAF8]" data-testid="crc-numero-display" />
              {lastCrc && <p className="text-xs text-[#9A9A9A] mt-1">Dernier CR : n°{lastCrc.numero || '?'} du {lastCrc.date ? formatD(lastCrc.date) : '?'}</p>}
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Période couverte *</Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div><p className="text-xs text-[#9A9A9A] mb-1">Du</p><Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="rounded-none" data-testid="crc-date-debut" /></div>
                <div><p className="text-xs text-[#9A9A9A] mb-1">Au</p><Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="rounded-none" data-testid="crc-date-fin" /></div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Réunion liée (optionnel)</Label>
              <Select value={reunionId} onValueChange={setReunionId}>
                <SelectTrigger className="rounded-none" data-testid="crc-reunion-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune réunion liée</SelectItem>
                  {reunions.map(r => <SelectItem key={r.id} value={r.id}>{r.titre} — {r.date_debut ? formatD(r.date_debut) : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-[#E0E0DB] pt-4">
              <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Prochaine réunion</Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div>
                  <p className="text-xs text-[#9A9A9A] mb-1">Date</p>
                  <Input type="date" value={prDate} onChange={e => setPrDate(e.target.value)} className="rounded-none" data-testid="crc-pr-date" />
                </div>
                <div>
                  <p className="text-xs text-[#9A9A9A] mb-1">Heure</p>
                  <Input type="time" value={prHeure} onChange={e => setPrHeure(e.target.value)} className="rounded-none" data-testid="crc-pr-heure" />
                </div>
              </div>
              <div className="mt-3">
                <p className="text-xs text-[#9A9A9A] mb-1">Lieu</p>
                <Input value={prLieu} onChange={e => setPrLieu(e.target.value)} className="rounded-none" data-testid="crc-pr-lieu" />
              </div>
              <div className="mt-3">
                <p className="text-xs text-[#9A9A9A] mb-1">Ordre du jour</p>
                <Textarea value={prOrdre} onChange={e => setPrOrdre(e.target.value)} rows={3} className="rounded-none" placeholder="Points à aborder lors de la prochaine réunion…" data-testid="crc-pr-ordre" />
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t border-[#E0E0DB]">
              <Button variant="outline" onClick={() => setStep(0)} className="rounded-none gap-1"><ArrowLeft size={14} /> Retour</Button>
              {/* A2 / B1 — le clic ne doit pas être transmis comme paramètre « forceFromPool » */}
              <Button onClick={() => goToStep2()} disabled={collecting} className="bg-[#C4773B] hover:bg-[#A5623A] rounded-none gap-2" data-testid="crc-goto-step2">
                {collecting ? 'Collecte…' : <>Suivant · Sélection <ArrowRight size={14} /></>}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 : Sélection */}
        {step === 2 && collected && (
          <div className="space-y-5" data-testid="crc-step-2">
            {/* M6.2 — Récap pool terrain (uniquement si pool non vide) */}
            {poolStats && poolStats.total_pool_messages > 0 && (
              <PoolPreparedRecap
                poolStats={poolStats}
                usePoolMode={usePoolMode}
                onSwitchToPool={() => switchPoolMode(true)}
                onSwitchToManual={() => switchPoolMode(false)}
              />
            )}

            {/* Résumé */}
            <div className="bg-[#FDF6EC] border-l-4 border-[#C4773B] p-3 text-xs text-[#6B4A24]">
              <p className="font-semibold uppercase tracking-wide mb-1">Données disponibles pour la période</p>
              <div className="grid grid-cols-5 gap-2">
                <div><b className="text-[#C4773B]">{collected.journal_textes?.length || 0}</b> notes</div>
                <div><b className="text-[#C4773B]">{collected.journal_photos?.length || 0}</b> photos</div>
                <div><b className="text-[#C4773B]">{(collected.reserves_ouvertes?.length || 0) + (collected.reserves_levees_periode?.length || 0)}</b> réserves</div>
                <div><b className="text-[#C4773B]">{collected.factures?.length || 0}</b> factures</div>
                <div><b className="text-[#C4773B]">{collected.taches_gantt?.length || 0}</b> tâches Gantt</div>
              </div>
            </div>

            {/* M2 Assistant — Recherche sémantique */}
            <SemanticSearchPanel
              chantierId={chantierId}
              dateDebut={dateDebut}
              dateFin={dateFin}
              selJournal={selJournal}
              setSelJournal={setSelJournal}
            />

            {/* Journal */}
            <SectionPicker
              title={
                <span className="flex items-center gap-2 flex-wrap">
                  <span>📋 Journal terrain</span>
                  {usePoolMode && poolStats && Object.keys(poolStats.by_type || {}).length > 0 && (
                    <span className="text-[10px] text-[#6B6B6B] font-normal normal-case tracking-normal">
                      ({renderTypeCounters(poolStats.by_type)})
                    </span>
                  )}
                </span>
              }
              count={collected.journal_textes?.length || 0}
              items={collected.journal_textes || []}
              selected={selJournal}
              onToggle={(id) => toggleSet(setSelJournal, id)}
              onToggleAll={() => toggleAll(setSelJournal, collected.journal_textes || [])}
              renderItem={(j) => (
                <>
                  {/* M6.7 — Étoile sur élément validé terrain en mode manuel */}
                  {!usePoolMode && j.ia_included_in_crc && (
                    <span title="Validé sur le terrain" className="text-yellow-500 text-sm" data-testid={`crc-item-validated-${j.id}`}>⭐</span>
                  )}
                  <span className="text-xs text-[#9A9A9A] whitespace-nowrap">{formatDT(j.created_at)}</span>
                  {/* M6.7 — Badge type d'analyse IA en mode pool */}
                  {usePoolMode && j.ia_type && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#FDF6EC] text-[#C4773B] font-bold whitespace-nowrap">
                      {iaTypeLabels[j.ia_type] || j.ia_type}
                    </span>
                  )}
                  <span className="text-sm text-[#2C2C2C] flex-1 truncate">{truncate(j.content, 120)}</span>
                  {j.author_name && <span className="text-xs text-[#6B6B6B]">{j.author_name}</span>}
                </>
              )}
              testid="crc-journal"
            />

            {/* Photos */}
            <PhotosPicker
              photos={collected.journal_photos || []}
              selected={selPhotos}
              onToggle={(id) => toggleSet(setSelPhotos, id)}
              onToggleAll={() => toggleAll(setSelPhotos, collected.journal_photos || [])}
            />

            {/* Réserves */}
            <SectionPicker
              title="⚠ Réserves / Observations"
              count={(collected.reserves_ouvertes?.length || 0) + (collected.reserves_levees_periode?.length || 0)}
              items={[...(collected.reserves_ouvertes || []), ...(collected.reserves_levees_periode || [])]}
              selected={selReserves}
              onToggle={(id) => toggleSet(setSelReserves, id)}
              onToggleAll={() => toggleAll(setSelReserves, [...(collected.reserves_ouvertes || []), ...(collected.reserves_levees_periode || [])])}
              renderItem={(r) => (
                <>
                  <span className={`text-[10px] px-2 py-0.5 ${r.status === 'levee' ? 'bg-[#E8F5E9] text-[#2D6A4F]' : 'bg-[#FFF3E0] text-[#C4773B]'}`}>{r.status}</span>
                  <span className="text-sm text-[#2C2C2C] flex-1 truncate">{truncate(r.description, 100)}</span>
                  {r.lot && <span className="text-xs bg-[#F5F5F3] px-2 py-0.5 text-[#6B6B6B]">Lot {r.lot}</span>}
                </>
              )}
              testid="crc-reserves"
            />

            {/* Planning */}
            <section className="border border-[#E0E0DB] p-3">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-sm flex-1">📊 Planning</h3>
                <span className="text-xs text-[#9A9A9A]">{collected.taches_gantt?.length || 0} tâches Gantt</span>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={includePlanning} onChange={e => setIncludePlanning(e.target.checked)} data-testid="crc-include-planning" />
                Inclure l'avancement planning dans le compte rendu
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-1">
                <input type="checkbox" checked={includeDetailTaches} onChange={e => setIncludeDetailTaches(e.target.checked)} data-testid="crc-include-detail-taches" disabled={!includePlanning} />
                <span className={!includePlanning ? 'opacity-50' : ''}>Détail tâche par tâche</span>
              </label>
            </section>

            {/* Factures */}
            <SectionPicker
              title="💰 Factures"
              count={collected.factures?.length || 0}
              items={collected.factures || []}
              selected={selFactures}
              onToggle={(id) => toggleSet(setSelFactures, id)}
              onToggleAll={() => toggleAll(setSelFactures, collected.factures || [])}
              renderItem={(f) => (
                <>
                  <span className="text-sm font-medium">{f.entreprise}</span>
                  <span className="text-sm text-[#C4773B] font-semibold whitespace-nowrap">{(f.montant_ht || 0).toLocaleString('fr-FR')} € HT</span>
                  <span className="text-xs text-[#6B6B6B]">{f.statut}</span>
                </>
              )}
              testid="crc-factures"
            />

            <div className="flex justify-between pt-4 border-t border-[#E0E0DB]">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-none gap-1"><ArrowLeft size={14} /> Retour</Button>
              <Button onClick={() => setStep(3)} className="bg-[#C4773B] hover:bg-[#A5623A] rounded-none gap-2" data-testid="crc-goto-step3">
                Suivant · Notes architecte <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 : Notes architecte */}
        {step === 3 && (
          <div className="space-y-4" data-testid="crc-step-3">
            <p className="text-sm text-[#6B6B6B]">Ces notes seront intégrées telles quelles par l'Assistant dans les sections dédiées du compte rendu.</p>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Décisions prises lors de cette réunion</Label>
              <Textarea value={decisions} onChange={e => setDecisions(e.target.value)} rows={3} className="rounded-none" placeholder="Ex : Validation des échantillons carrelage salle de bain. Choix du type de volet roulant…" data-testid="crc-notes-decisions" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Instructions aux entreprises</Label>
              <Textarea value={instructions} onChange={e => setInstructions(e.target.value)} rows={3} className="rounded-none" placeholder="Ex : Couvreur — livraison matériaux avant lundi. Électricien — passage gaines avant vendredi…" data-testid="crc-notes-instructions" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Observations générales</Label>
              <Textarea value={observations} onChange={e => setObservations(e.target.value)} rows={3} className="rounded-none" placeholder="Remarques sur le déroulé, la coordination, les contraintes…" data-testid="crc-notes-observations" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Points à surveiller pour la prochaine réunion</Label>
              <Textarea value={pointsSurveille} onChange={e => setPointsSurveille(e.target.value)} rows={3} className="rounded-none" placeholder="Ex : Vérifier la pose du carrelage, valider le coloris des murs…" data-testid="crc-notes-points" />
            </div>

            <div className="flex justify-between pt-4 border-t border-[#E0E0DB]">
              <Button variant="outline" onClick={() => setStep(2)} className="rounded-none gap-1"><ArrowLeft size={14} /> Retour</Button>
              <Button onClick={handleGenerate} disabled={generating} className="bg-[#C4773B] hover:bg-[#A5623A] rounded-none gap-2" data-testid="crc-generate-btn">
                <Sparkle size={16} weight="fill" />
                {generating ? 'Rédaction en cours…' : 'Générer le compte rendu'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SectionPicker({ title, count, items, selected, onToggle, onToggleAll, renderItem, testid }) {
  if (!items || items.length === 0) {
    return (
      <section className="border border-[#E0E0DB] p-3" data-testid={`${testid}-empty`}>
        <h3 className="font-semibold text-sm text-[#9A9A9A]">{title} <span className="text-xs font-normal">(aucune donnée pour cette période)</span></h3>
      </section>
    );
  }
  const allSel = items.every(i => selected.has(i.id));
  return (
    <section className="border border-[#E0E0DB]" data-testid={`${testid}-section`}>
      <div className="flex items-center gap-3 px-3 py-2 bg-[#F5F5F3] cursor-pointer" onClick={onToggleAll}>
        <input type="checkbox" checked={allSel} readOnly data-testid={`${testid}-toggle-all`} />
        <h3 className="font-semibold text-sm flex-1">{title} <span className="text-xs text-[#6B6B6B] font-normal">({count})</span></h3>
        <span className="text-xs text-[#9A9A9A]">{selected.size}/{count} cochés</span>
      </div>
      <div className="max-h-60 overflow-y-auto">
        {items.map((it) => (
          <label key={it.id} className="flex items-center gap-2 px-3 py-2 border-t border-[#F0F0EC] hover:bg-[#FDF6EC] cursor-pointer" data-testid={`${testid}-item`}>
            <input type="checkbox" checked={selected.has(it.id)} onChange={() => onToggle(it.id)} />
            {renderItem(it)}
          </label>
        ))}
      </div>
    </section>
  );
}

function PhotosPicker({ photos, selected, onToggle, onToggleAll }) {
  if (!photos || photos.length === 0) {
    return (
      <section className="border border-[#E0E0DB] p-3" data-testid="crc-photos-empty">
        <h3 className="font-semibold text-sm text-[#9A9A9A]">📷 Photos <span className="text-xs font-normal">(aucune photo pour cette période)</span></h3>
      </section>
    );
  }
  const allSel = photos.every(p => selected.has(p.id));
  return (
    <section className="border border-[#E0E0DB]" data-testid="crc-photos-section">
      <div className="flex items-center gap-3 px-3 py-2 bg-[#F5F5F3] cursor-pointer" onClick={onToggleAll}>
        <input type="checkbox" checked={allSel} readOnly />
        <h3 className="font-semibold text-sm flex-1">📷 Photos <span className="text-xs text-[#6B6B6B] font-normal">({photos.length})</span></h3>
        <span className="text-xs text-[#9A9A9A]">{selected.size}/{photos.length} cochés</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 p-3 max-h-80 overflow-y-auto">
        {photos.map(p => (
          <label key={p.id} className={`relative aspect-square cursor-pointer border-2 ${selected.has(p.id) ? 'border-[#C4773B]' : 'border-transparent hover:border-[#E0E0DB]'}`} data-testid="crc-photo-item">
            <input type="checkbox" className="absolute top-1 left-1 z-10" checked={selected.has(p.id)} onChange={() => onToggle(p.id)} />
            <img src={p.thumbnail_url || p.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate">{formatDT(p.created_at)}</div>
          </label>
        ))}
      </div>
    </section>
  );
}

// M2 Assistant — Panneau de recherche sémantique dans le wizard CRC
function SemanticSearchPanel({ chantierId, dateDebut, dateFin, selJournal, setSelJournal }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) { setResults(null); return; }
    setSearching(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/search-similar`,
        {
          query: q,
          k: 30,
          date_from: dateDebut || undefined,
          date_to: dateFin || undefined,
          channels: ['terrain', 'interne', 'moe', 'entreprises'],
        },
        { withCredentials: true }
      );
      setResults(data.results || []);
    } catch {
      toast.error('Recherche sémantique indisponible');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const toggleSelect = (id) => {
    setSelJournal((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="border border-purple-200 bg-purple-50 p-3 rounded" data-testid="crc-semantic-search">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={14} className="text-purple-700" weight="fill" />
        <Label className="text-xs font-bold uppercase tracking-wider text-purple-700">
          Recherche sémantique (optionnel)
        </Label>
      </div>
      <p className="text-[11px] text-purple-600 mb-2">
        Trouve rapidement les messages liés à un sujet (ex: &quot;fissures cuisine R+1&quot;, &quot;retard plâtrerie&quot;, &quot;validation carrelage&quot;)
      </p>
      <div className="flex gap-2">
        <Input
          placeholder="Mots-clés ou phrase..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
          className="rounded-none flex-1"
          data-testid="crc-semantic-search-input"
        />
        <Button
          onClick={handleSearch}
          disabled={searching || !query.trim()}
          className="rounded-none bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="crc-semantic-search-submit"
        >
          {searching ? '...' : 'Rechercher'}
        </Button>
      </div>

      {results && results.length === 0 && (
        <p className="text-xs text-purple-600 mt-2" data-testid="crc-semantic-no-results">
          Aucun résultat pertinent trouvé.
        </p>
      )}

      {results && results.length > 0 && (
        <div className="mt-3 max-h-64 overflow-y-auto border border-purple-100 bg-white rounded" data-testid="crc-semantic-results">
          {results.map((r) => {
            const text = r.transcription_text || r.content || '';
            const isSelected = selJournal.has(r.id);
            return (
              <div
                key={r.id}
                className="px-2 py-2 border-b border-purple-100 last:border-b-0 flex items-start gap-2 text-xs hover:bg-purple-50"
                data-testid={`crc-semantic-result-${r.id}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(r.id)}
                  className="mt-1"
                  data-testid={`crc-semantic-select-${r.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-[10px] text-gray-500">
                      {(r.created_at || '').substring(0, 16).replace('T', ' ')}
                    </span>
                    {r.author_name && <span className="text-[10px] font-medium">{r.author_name}</span>}
                    {r.type === 'vocal' && (
                      <span className="text-[10px] bg-purple-100 text-purple-700 px-1 rounded">Vocal</span>
                    )}
                    {r.source === 'jarvis_whatsapp_import' && (
                      <span className="text-[10px] bg-green-100 text-green-700 px-1 rounded">WhatsApp</span>
                    )}
                  </div>
                  <p className="text-gray-700 truncate">
                    {text.substring(0, 120)}{text.length > 120 ? '…' : ''}
                  </p>
                </div>
                <span className="text-[10px] text-purple-600 font-semibold whitespace-nowrap">
                  {Math.round(r.similarity * 100)}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// M6.6 — Labels FR pour les types d'analyse IA (utilisés dans badges + compteurs)
const iaTypeLabels = {
  reserve: 'Rés.',
  tache: 'Action',
  observation: 'Obs.',
  validation: 'Val.',
  decision: 'Déc.',
  demande_client: 'Dem.',
  question_entreprise: 'Q.',
  incident: 'Inc.',
  non_conformite: 'NC',
  autre: 'Autre',
};

const iaTypeFullLabels = {
  reserve: 'réserves',
  tache: 'actions',
  observation: 'observations',
  validation: 'validations',
  decision: 'décisions',
  demande_client: 'demandes client',
  question_entreprise: 'questions ent.',
  incident: 'incidents',
  non_conformite: 'non-conformités',
  autre: 'autres',
};

// M6.6 — Compteurs courts pour le titre du Journal (top 5)
function renderTypeCounters(byType) {
  if (!byType || Object.keys(byType).length === 0) return '';
  return Object.entries(byType)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([type, count]) => `${count} ${iaTypeLabels[type] || type}`)
    .join(' · ');
}

// M6.2 — Récap pool éléments validés terrain + boutons de bascule pool/manuel
function PoolPreparedRecap({ poolStats, usePoolMode, onSwitchToPool, onSwitchToManual }) {
  const total = poolStats.total_pool_messages || 0;
  const byType = poolStats.by_type || {};
  const sortedTypes = Object.entries(byType).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div
      className={`border-2 ${usePoolMode ? 'border-[#C4773B] bg-[#FDF6EC]' : 'border-[#1E3A5F] bg-[#F8F7F4]'} p-4`}
      data-testid="crc-pool-recap"
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl">{usePoolMode ? '🌟' : '✨'}</span>
        <div className="flex-1">
          <p className="font-bold text-sm uppercase tracking-wider text-[#1E3A5F] mb-1">
            Préparé sur le terrain
          </p>
          <p className="text-xs text-[#6B6B6B] mb-3">
            <span className="font-bold text-[#C4773B] text-base" data-testid="crc-pool-total">{total}</span> élément{total > 1 ? 's' : ''} validé{total > 1 ? 's' : ''} et marqué{total > 1 ? 's' : ''} &laquo;&nbsp;Inclure dans le prochain CR&nbsp;&raquo;
          </p>

          {sortedTypes.length > 0 && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3 text-xs">
              {sortedTypes.map(([type, count]) => (
                <div key={type} className="flex items-center gap-1.5 text-[#6B6B6B]">
                  <span className="font-bold text-[#1E3A5F]">{count}</span>
                  <span>{iaTypeFullLabels[type] || type}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E0E0DB]">
            {usePoolMode ? (
              <>
                <button
                  className="bg-[#C4773B] text-white px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded-none cursor-default"
                  data-testid="crc-pool-mode-active"
                  type="button"
                >
                  ✓ Mode pool actif
                </button>
                <button
                  onClick={onSwitchToManual}
                  className="bg-white text-[#6B6B6B] border border-[#E0E0DB] px-3 py-1.5 text-xs font-medium uppercase tracking-wider hover:border-[#1E3A5F] rounded-none"
                  data-testid="crc-switch-to-manual"
                  type="button"
                >
                  Sélection manuelle
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onSwitchToPool}
                  className="bg-[#C4773B] text-white px-3 py-1.5 text-xs font-medium uppercase tracking-wider hover:bg-[#A5623A] rounded-none"
                  data-testid="crc-switch-to-pool"
                  type="button"
                >
                  → Utiliser le pool terrain
                </button>
                <button
                  className="bg-white text-[#1E3A5F] border-2 border-[#1E3A5F] px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded-none cursor-default"
                  data-testid="crc-manual-mode-active"
                  type="button"
                >
                  ✓ Mode manuel actif
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
